/**
 * Connecting this account to the Telegram bot.
 *
 * The link is made from the phone, not from here: this asks the server for a
 * code and the person sends it to the bot, which is the only way the bot learns
 * which chat belongs to them — and the only way it is allowed to write to them
 * at all, since Telegram lets a bot answer but never open a conversation.
 *
 * Renders nothing when the install has no bot configured: there would be
 * nowhere to send the code.
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { telegramLinkCode, telegramStatus, telegramUnlink } from "../../modules/telegramApi";

const useStyles = makeStyles({
  card: { margin: "0rem 0rem 0.5rem 0.5rem" },
  wrapper: { padding: "2rem" },
  title: { fontSize: 26, fontWeight: 540 },
  body: { marginTop: "1rem", textAlign: "left" },
  line: { marginBottom: "0.5rem" },
  code: {
    display: "inline-block",
    fontFamily: "monospace",
    fontSize: 22,
    letterSpacing: "0.15em",
    padding: "0.5rem 1rem",
    margin: "0.5rem 0",
    borderRadius: 4,
    backgroundColor: "#2f2f2f",
    color: "#fff",
  },
  hint: { fontSize: 13, opacity: 0.75 },
  actions: { marginTop: "1rem", display: "flex", gap: "0.75rem" },
});

export default function CardTelegramLink({ createSnackbar }) {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    telegramStatus().then((response) => {
      if (!cancelled && response.ok) setStatus(response.data);
    });
    return () => { cancelled = true; };
  }, []);

  async function askForCode() {
    setIsBusy(true);
    const response = await telegramLinkCode();
    setIsBusy(false);
    if (!response.ok) {
      createSnackbar?.("The code could not be created.", "error", false);
      return;
    }
    setCode(response.data);
  }

  async function disconnect() {
    setIsBusy(true);
    const response = await telegramUnlink();
    setIsBusy(false);
    if (!response.ok) {
      createSnackbar?.("The link could not be removed.", "error", false);
      return;
    }
    setCode(null);
    setStatus((current) => ({ ...(current || {}), linked: false }));
    createSnackbar?.("Telegram link removed.", "success", false);
  }

  // Nothing to offer until the server says there is a bot to talk to.
  if (!status || !status.configured) return null;

  return (
    <Card className={classes.card}>
      <div className={classes.wrapper}>
        <Typography className={classes.title} align="left">
          Telegram
        </Typography>

        <div className={classes.body}>
          {status.linked ? (
            <>
              <Typography className={classes.line}>
                Linked. The bot writes to you when one of your agent sessions finishes.
              </Typography>
              <div className={classes.actions}>
                <Button color="primary" disabled={isBusy} onClick={disconnect}>
                  Remove link
                </Button>
              </div>
            </>
          ) : code ? (
            <>
              <Typography className={classes.line}>
                Send this to the bot:
              </Typography>
              <div className={classes.code}>/start {code.code}</div>
              <Typography className={classes.hint}>
                {code.botUsername ? `Bot: @${code.botUsername}. ` : ""}
                The code is valid for {Math.round(code.expiresInMs / 60000)} minutes. Refresh this
                page after sending it.
              </Typography>
            </>
          ) : (
            <>
              <Typography className={classes.line}>
                Hear on Telegram when one of your agent sessions finishes, and start new ones
                from there.
              </Typography>
              <div className={classes.actions}>
                <Button color="primary" variant="contained" disabled={isBusy} onClick={askForCode}>
                  Link Telegram
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
